import useSWR from "swr";
import { Paginated, PaginationQuery } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { LockLight } from "./types";

/**
 * Inputs backing the same lock-compatibility rules the backend already
 * applies during configuration generation — geometry (filterCompatibleLocks)
 * plus an already-chosen header/joist fastener (filterByFastener, the
 * reverse of how a chosen lock narrows the fastener selects) — passed here
 * so the lock select can be narrowed to compatible items too, without
 * duplicating those rules on the frontend.
 */
export type LockGeometryFilter = {
  headerOrientation?: string | null;
  headerB?: number | null;
  headerH?: number | null;
  joistB?: number | null;
  joistH?: number | null;
  /** Distance (mm) from the joist top edge to the connector; unset = each lock's c_min (backend default). */
  connectorTopDistance?: number | null;
  /** Column header only: with "offset" the c_min is checked on joistTopOffset + connectorTopDistance. */
  joistTopAlignment?: string | null;
  /** Joist top offset (mm), offset mode only; unset = each lock's c_min. */
  joistTopOffset?: number | null;
  joistMaterialGradeId?: string | null;
  /** Already-chosen header/joist fastener — narrows locks to those compatible with it (the reverse of how a chosen lock narrows the fastener selects). */
  headerFastenerItemId?: string | null;
  joistFastenerItemId?: string | null;
  /** Narrows to the locks that can replace this one on the header side only (same height, wider). */
  widerHeaderThanLockItemId?: string | null;
};

export const useLocks = (
  query: PaginationQuery,
  signal: AbortSignal,
  isAdmin?: boolean,
  firstLockItemId?: string | null,
  geometry?: LockGeometryFilter,
) => {
  const api = useApi();
  const prefix = isAdmin ? "/admin" : "";
  let url = buildUrl(`${prefix}/modules/lock_calculator/selects/lock`, query);
  const extraParams: Record<string, string> = {};
  if (firstLockItemId) extraParams["firstLockItemId"] = firstLockItemId;
  if (geometry) {
    for (const [key, value] of Object.entries(geometry)) {
      if (value !== null && value !== undefined) extraParams[key] = String(value);
    }
  }
  const search = new URLSearchParams(extraParams).toString();
  if (search) url += (url.includes("?") ? "&" : "?") + search;

  return useSWR<Paginated<LockLight>, CustomError, string>(url, (requestUrl) =>
    api.get<Paginated<LockLight>>({ url: requestUrl, signal }),
  );
};
